import React from "react";

import isEqual from "fast-deep-equal";

import { LexerOptions, lexizeText } from "@app/component/Lexer/lexizeText";
import LongContentParagraph, {
  LongContentParagraphProps,
} from "@app/component/typography/LongContentParagraph";

export interface FormattedLongContentParagraphProps extends Omit<
  LongContentParagraphProps,
  "children"
> {
  children: string;
  lexerProps?: LexerOptions;
}

// LongContentParagraph doesn't work with children properly (and that's understandable)
// So for example, if we call
//   <LongContentParagraph>
//     <MultilineText wrapLinesWith={Lexer}>
//       {text}
//     </MultilineText>
//   </LongContentParagraph>
//
// It will see it as if it had a single child instead of multiple. And since it's a single
// child, it can't split it into rows properly and then hide all rows beyond the constraint.
//
// However, LongContentParagraph can work with arrays properly. So here we pre-process text
// splitting into lines and adding decorators from lexer, but keeping it in an array form
// avoiding JSX tree structure.
//
// Using isEqual to compare props instead of using useMemo for `content`, mostly because
// of the lexerProps prop, which is typically re-created on every render. Because it is
// typically used as `lexerProps={{ users, teams }}`.
const FormattedLongContentParagraph = React.memo(
  ({ children, lexerProps, ...props }: FormattedLongContentParagraphProps) => {
    const content = children
      .split("\n")
      .flatMap((line, index) => [
        lexizeText(line, lexerProps, `line-${index}__`),
        <br key={`br_${index}`} />,
      ]);

    return <LongContentParagraph {...props}>{content}</LongContentParagraph>;
  },
  isEqual
);

export default FormattedLongContentParagraph;
